import React from 'react';
import { useExpensesImport } from '../hooks/ImportExpenses';
import AnimatedBackground from '../../utils/AnimatedBackground';
import TopBar from '../../utils/TopBar';
import Footer from '../../utils/Footer';

const ImportExpenses: React.FC = () => {
  const {
    currentStep,
    totalSteps,
    formData,
    handleOptionSelect,
    handleFileSelect,
    handleNextStep,
    handlePrevStep,
    restartSelection,
    handleSubmit,
    navigate,
    isLoading
  } = useExpensesImport();

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div>
            <h3 className="text-xl font-semibold mb-6">Select file type:</h3>
            <div className="flex justify-center space-x-4">
              <button
                type="button"
                className={`px-6 py-3 rounded-md transition-colors ${
                  formData.fileType === 'credit_card' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-blue-500/20 hover:bg-blue-500/30 text-blue-100'
                }`}
                onClick={() => handleOptionSelect('credit_card', 'fileType')}
              >
                Credit card statement
              </button>
              <button
                type="button"
                className={`px-6 py-3 rounded-md transition-colors ${
                  formData.fileType === 'bank_account' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-blue-500/20 hover:bg-blue-500/30 text-blue-100'
                }`}
                onClick={() => handleOptionSelect('bank_account', 'fileType')}
              >
                Bank account statement
              </button>
            </div>
          </div>
        );
      case 2:
        return (
          <div>
            <h3 className="text-xl font-semibold mb-6">Select file format:</h3>
            <div className="flex justify-center space-x-4">
              <button
                type="button"
                className={`px-6 py-3 rounded-md transition-colors ${
                  formData.fileFormat === 'xlsx' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-blue-500/20 hover:bg-blue-500/30 text-blue-100'
                }`}
                onClick={() => handleOptionSelect('xlsx', 'fileFormat')}
              >
                Excel (.xlsx)
              </button>
              <button
                type="button"
                className={`px-6 py-3 rounded-md transition-colors ${
                  formData.fileFormat === 'pdf' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-blue-500/20 hover:bg-blue-500/30 text-blue-100'
                }`}
                onClick={() => handleOptionSelect('pdf', 'fileFormat')}
              >
                PDF (.pdf)
              </button>
            </div>
          </div>
        );
      case 3:
        return formData.fileType === 'credit_card' && formData.fileFormat === 'pdf' ? (
          <div>
            <h3 className="text-xl font-semibold mb-6">Select your bank:</h3>
            <div className="flex justify-center space-x-4">
              <button
                type="button"
                className={`px-6 py-3 rounded-md transition-colors ${
                  formData.bank === 'nubank' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-blue-500/20 hover:bg-blue-500/30 text-blue-100'
                }`}
                onClick={() => handleOptionSelect('nubank', 'bank')}
              >
                Nubank
              </button>
              <button
                type="button"
                className={`px-6 py-3 rounded-md transition-colors ${
                  formData.bank === 'bradesco' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-blue-500/20 hover:bg-blue-500/30 text-blue-100'
                }`}
                onClick={() => handleOptionSelect('bradesco', 'bank')}
              >
                Bradesco
              </button>
            </div>
          </div>
        ) : (
          <div>
            <h3 className="text-lg font-semibold mb-4">Select file:</h3>
            <div className="flex items-center justify-center w-full">
              <label htmlFor="file-upload" className="flex flex-col items-center justify-center w-full h-48 border-2 border-gray-700 border-dashed rounded-lg cursor-pointer bg-black/50 hover:bg-gray-900/50 transition-all">
                <div className="flex flex-col items-center justify-center pt-4 pb-4">
                  <svg className="w-8 h-8 mb-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
                  </svg>
                  <p className="mb-1 text-sm text-gray-400"><span className="font-semibold">Click to upload</span> or drag and drop</p>
                  <p className="text-xs text-gray-500">XLSX or PDF (MAX. 10MB)</p>
                </div>
                <input
                  id="file-upload"
                  type="file"
                  className="hidden"
                  onChange={handleFileSelect}
                  accept=".xlsx,.pdf"
                  multiple
                />
              </label>
            </div>
            {formData.files && formData.files.length > 0 && (
              <p className="mt-2 text-sm text-gray-400">
                Selected files: {formData.files.map((file) => file.name).join(', ')}
              </p>
            )}
          </div>
        );
      case 4:
        return (
          <div>
            <h3 className="text-lg font-semibold mb-4">Select file:</h3>
            <div className="flex items-center justify-center w-full">
              <label htmlFor="file-upload" className="flex flex-col items-center justify-center w-full h-48 border-2 border-gray-700 border-dashed rounded-lg cursor-pointer bg-black/50 hover:bg-gray-900/50 transition-all">
                <div className="flex flex-col items-center justify-center pt-4 pb-4">
                  <svg className="w-8 h-8 mb-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
                  </svg>
                  <p className="mb-1 text-sm text-gray-400"><span className="font-semibold">Click to upload</span> or drag and drop</p>
                  <p className="text-xs text-gray-500">XLSX or PDF (MAX. 10MB)</p>
                </div>
                <input
                  id="file-upload"
                  type="file"
                  className="hidden"
                  onChange={handleFileSelect}
                  accept=".xlsx,.pdf"
                  multiple
                />
              </label>
            </div>
            {formData.files && formData.files.length > 0 && (
              <p className="mt-2 text-sm text-gray-400">
                Selected files: {formData.files.map((file) => file.name).join(', ')}
              </p>
            )}
          </div>
        );
      case 5:
        return (
          <div>
            <h3 className="text-xl font-semibold mb-6">Your selections:</h3>
            <div className="bg-gray-700/50 p-6 rounded-lg mb-6">
              <div className="space-y-3 text-left">
                <div className="flex justify-between">
                  <span className="text-gray-300">File type:</span>
                  <span className="text-white font-medium">{formData.fileType.replace('_', ' ').toUpperCase()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">File format:</span>
                  <span className="text-white font-medium">{formData.fileFormat.toUpperCase()}</span>
                </div>
                {formData.bank && (
                  <div className="flex justify-between">
                    <span className="text-gray-300">Bank:</span>
                    <span className="text-white font-medium">{formData.bank.toUpperCase()}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-300">File name(s):</span>
                  <span className="text-white font-medium">
                    {formData.files.length
                      ? formData.files.map((file) => file.name).join(', ')
                      : 'No file selected'}
                  </span>
                </div>
              </div>
            </div>
            <h4 className="text-lg font-medium text-white mb-6">Are these selections correct?</h4>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex flex-col relative">
      <AnimatedBackground />
      {isLoading && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-black/80 p-8 rounded-lg border border-gray-700 flex flex-col items-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mb-4"></div>
            <p className="text-white text-lg">Processing your file...</p>
          </div>
        </div>
      )}
      <div className="relative z-10 flex flex-col min-h-screen">
        <TopBar 
          title="Import Expenses"
          subtitle="Add new expense records to your account"
          isLoading={isLoading}
        />
        
        <main className="flex-grow flex items-center justify-center py-12 relative z-10">
          <div className="w-full max-w-2xl">
            <div className="bg-black border border-gray-700 shadow-lg rounded-lg mx-4">
              <div className="px-8 py-6">
                <form onSubmit={handleSubmit}>
                  <div className="text-white text-center space-y-6">
                    {renderStep()}
                  </div>
                  <div className="mt-8 flex justify-center space-x-4">
                    {currentStep === 1 ? (
                      <button
                        type="button"
                        onClick={() => navigate('/expenses/dashboard')}
                        className="px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-500 transition-colors"
                      >
                        Back to Table
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={handlePrevStep}
                        className="px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-500 transition-colors"
                      >
                        Back
                      </button>
                    )}
                    {currentStep < totalSteps ? (
                      <button
                        type="button"
                        onClick={handleNextStep}
                        className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-500 transition-colors"
                        disabled={
                          (currentStep === 1 && !formData.fileType) ||
                          (currentStep === 2 && !formData.fileFormat) ||
                          (currentStep === 3 && formData.fileType === 'credit_card' && formData.fileFormat === 'pdf' && !formData.bank) ||
                          (currentStep === 4 && formData.files.length === 0)
                        }
                      >
                        Next
                      </button>
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={restartSelection}
                          className="px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-500 transition-colors"
                        >
                          Restart Selection
                        </button>
                        <button
                          type="submit"
                          className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-500 transition-colors"
                        >
                          Preview Import
                        </button>
                      </>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </div>
  );
};

export default ImportExpenses;

